type ApiError = {
  message: string;
  code?: string;
  details?: unknown;
};

type ApiResult<T = any> = {
  data: T | null;
  error: ApiError | null;
  count?: number | null;
};

type Filter = {
  column: string;
  operator: string;
  value: unknown;
};

type Order = {
  column: string;
  ascending?: boolean;
  nullsFirst?: boolean;
};

const API_BASE = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");

function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

function publicOrigin(): string {
  if (API_BASE.startsWith("http://") || API_BASE.startsWith("https://")) return API_BASE;
  if (typeof window !== "undefined") return window.location.origin;
  return "";
}

function normalizeError(raw: unknown): ApiError {
  if (raw && typeof raw === "object" && "message" in raw) {
    return raw as ApiError;
  }
  if (typeof raw === "string") return { message: raw };
  return { message: "Request failed", details: raw };
}

// The serialized query body sent to /api/db/:table (and each entry of /api/db/batch).
type RequestBody = {
  action: string;
  select: string;
  payload: unknown;
  filters: Filter[];
  or: string[];
  orders: Order[];
  limit: number | null;
  count: string | null;
  head: boolean;
  returning: boolean;
  single: boolean;
  maybeSingle: boolean;
  onConflict: string | undefined;
};

// One un-batched request to the single-query endpoint. Also the fallback path if
// batching is unavailable (e.g. an older server) or the batch request fails.
async function singleRequest<T = any>(
  table: string,
  body: RequestBody,
  signal: AbortSignal | null
): Promise<ApiResult<T>> {
  try {
    const res = await fetch(apiUrl(`/api/db/${encodeURIComponent(table)}`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal: signal || undefined,
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => null);
    if (!res.ok) {
      return { data: null, error: normalizeError(json?.error || json || res.statusText), count: null };
    }
    return {
      data: (json?.data ?? null) as T | null,
      error: json?.error ?? null,
      count: json?.count ?? null,
    };
  } catch (err) {
    return { data: null, error: normalizeError(err), count: null };
  }
}

// ── Select micro-batching ──────────────────────────────────────────────────
// Concurrent selects issued in the same tick (e.g. every homepage section
// mounting at once) are coalesced into a single /api/db/batch round-trip that
// runs them in parallel server-side. Writes never batch. If batching fails for
// any reason we transparently replay each query individually, so behaviour can
// only degrade to the old fan-out — never break.
const BATCH_TIMEOUT_MS = 15_000;

type PendingSelect = {
  table: string;
  body: RequestBody;
  signal: AbortSignal | null;
  resolve: (result: ApiResult) => void;
  settled: boolean;
};

let selectQueue: PendingSelect[] = [];
let flushScheduled = false;

function settlePending(pending: PendingSelect, result: ApiResult): void {
  if (pending.settled) return;
  pending.settled = true;
  pending.resolve(result);
}

function scheduleFlush(): void {
  if (flushScheduled) return;
  flushScheduled = true;
  // Microtask flush: runs after the current synchronous burst of effect
  // callbacks has finished enqueueing, capturing the whole wave in one batch.
  queueMicrotask(flushSelectBatch);
}

async function flushSelectBatch(): Promise<void> {
  flushScheduled = false;
  const batch = selectQueue;
  selectQueue = [];
  const active = batch.filter((pending) => !pending.settled);
  if (active.length === 0) return;

  const runIndividually = () =>
    Promise.all(
      active.map((pending) =>
        singleRequest(pending.table, pending.body, pending.signal).then((result) =>
          settlePending(pending, result)
        )
      )
    );

  // A lone query gains nothing from the batch envelope — and keeps its own
  // abort signal wired straight to the network.
  if (active.length === 1) {
    await runIndividually();
    return;
  }

  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), BATCH_TIMEOUT_MS);
    const res = await fetch(apiUrl(`/api/db/batch`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal: controller.signal,
      body: JSON.stringify({ queries: active.map((p) => ({ table: p.table, body: p.body })) }),
    });
    clearTimeout(timer);

    if (res.status === 404) {
      // Server predates the batch endpoint — fall back to individual requests.
      await runIndividually();
      return;
    }
    const json = await res.json().catch(() => null);
    if (!res.ok || !json || !Array.isArray(json.results) || json.results.length !== active.length) {
      await runIndividually();
      return;
    }
    active.forEach((pending, i) => {
      const result = json.results[i] || { data: null, error: null, count: null };
      settlePending(pending, {
        data: result.data ?? null,
        error: result.error ?? null,
        count: result.count ?? null,
      });
    });
  } catch {
    await runIndividually();
  }
}

function enqueueSelect<T = any>(
  table: string,
  body: RequestBody,
  signal: AbortSignal | null
): Promise<ApiResult<T>> {
  return new Promise<ApiResult<T>>((resolve) => {
    const pending: PendingSelect = {
      table,
      body,
      signal,
      resolve: resolve as (result: ApiResult) => void,
      settled: false,
    };
    if (signal) {
      if (signal.aborted) {
        pending.settled = true;
        resolve({ data: null, error: { message: "AbortError" }, count: null });
        return;
      }
      // Honor a caller timeout even while batched: settle this one query early;
      // the shared batch request keeps running for the others.
      signal.addEventListener(
        "abort",
        () => settlePending(pending, { data: null, error: { message: "AbortError" }, count: null }),
        { once: true }
      );
    }
    selectQueue.push(pending);
    scheduleFlush();
  });
}

class LocalQueryBuilder<T = any> implements PromiseLike<ApiResult<T>> {
  private table: string;
  private action: "select" | "insert" | "update" | "delete" | "upsert" = "select";
  private selected = "*";
  private payload: unknown;
  private filters: Filter[] = [];
  private orExpressions: string[] = [];
  private orders: Order[] = [];
  private limitValue: number | null = null;
  private countMode: string | null = null;
  private headMode = false;
  private returning = false;
  private singleMode: "single" | "maybeSingle" | null = null;
  private onConflict: string | undefined;
  private signal: AbortSignal | null = null;

  constructor(table: string) {
    this.table = table;
  }

  select(columns = "*", options?: { count?: string; head?: boolean }) {
    this.selected = columns || "*";
    this.countMode = options?.count || null;
    this.headMode = Boolean(options?.head);
    if (this.action !== "select") this.returning = true;
    return this;
  }

  insert(payload: unknown) {
    this.action = "insert";
    this.payload = payload;
    return this;
  }

  update(payload: unknown) {
    this.action = "update";
    this.payload = payload;
    return this;
  }

  delete() {
    this.action = "delete";
    return this;
  }

  upsert(payload: unknown, options?: { onConflict?: string }) {
    this.action = "upsert";
    this.payload = payload;
    this.onConflict = options?.onConflict;
    return this;
  }

  eq(column: string, value: unknown) {
    this.filters.push({ column, operator: "eq", value });
    return this;
  }

  neq(column: string, value: unknown) {
    this.filters.push({ column, operator: "neq", value });
    return this;
  }

  ilike(column: string, value: unknown) {
    this.filters.push({ column, operator: "ilike", value });
    return this;
  }

  gte(column: string, value: unknown) {
    this.filters.push({ column, operator: "gte", value });
    return this;
  }

  gt(column: string, value: unknown) {
    this.filters.push({ column, operator: "gt", value });
    return this;
  }

  lte(column: string, value: unknown) {
    this.filters.push({ column, operator: "lte", value });
    return this;
  }

  lt(column: string, value: unknown) {
    this.filters.push({ column, operator: "lt", value });
    return this;
  }

  in(column: string, value: unknown[]) {
    this.filters.push({ column, operator: "in", value });
    return this;
  }

  is(column: string, value: unknown) {
    this.filters.push({ column, operator: "is", value });
    return this;
  }

  not(column: string, operator: string, value: unknown) {
    this.filters.push({ column, operator: `not.${operator}`, value });
    return this;
  }

  or(expression: string) {
    this.orExpressions.push(expression);
    return this;
  }

  order(column: string, options?: { ascending?: boolean; nullsFirst?: boolean }) {
    this.orders.push({ column, ascending: options?.ascending, nullsFirst: options?.nullsFirst });
    return this;
  }

  limit(value: number) {
    this.limitValue = value;
    return this;
  }

  single() {
    this.singleMode = "single";
    return this;
  }

  maybeSingle() {
    this.singleMode = "maybeSingle";
    return this;
  }

  abortSignal(signal: AbortSignal) {
    this.signal = signal;
    return this;
  }

  private buildRequestBody(): RequestBody {
    return {
      action: this.action,
      select: this.selected,
      payload: this.payload,
      filters: this.filters,
      or: this.orExpressions,
      orders: this.orders,
      limit: this.limitValue,
      count: this.countMode,
      head: this.headMode,
      returning: this.returning,
      single: this.singleMode === "single",
      maybeSingle: this.singleMode === "maybeSingle",
      onConflict: this.onConflict,
    };
  }

  async execute(): Promise<ApiResult<T>> {
    const body = this.buildRequestBody();
    // Only reads are coalesced; writes go straight out to preserve ordering.
    if (this.action === "select") {
      return enqueueSelect<T>(this.table, body, this.signal);
    }
    return singleRequest<T>(this.table, body, this.signal);
  }

  then<TResult1 = ApiResult<T>, TResult2 = never>(
    onfulfilled?: ((value: ApiResult<T>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null
  ): Promise<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected);
  }

  catch<TResult = never>(
    onrejected?: ((reason: unknown) => TResult | PromiseLike<TResult>) | null
  ): Promise<ApiResult<T> | TResult> {
    return this.execute().catch(onrejected);
  }

  finally(onfinally?: (() => void) | null): Promise<ApiResult<T>> {
    return this.execute().finally(onfinally || undefined);
  }
}

class LocalStorageBucket {
  private bucket: string;

  constructor(bucket: string) {
    this.bucket = bucket;
  }

  async upload(
    path: string,
    file: Blob,
    options?: { upsert?: boolean; contentType?: string; cacheControl?: string }
  ): Promise<ApiResult<{ path: string; fullPath: string }>> {
    try {
      const form = new FormData();
      const filename = path.split("/").pop() || "upload";
      form.append("file", file, filename);
      if (options?.contentType) form.append("contentType", options.contentType);
      if (options?.cacheControl) form.append("cacheControl", options.cacheControl);

      const params = new URLSearchParams({
        path,
        upsert: String(Boolean(options?.upsert)),
      });
      const res = await fetch(apiUrl(`/api/storage/${encodeURIComponent(this.bucket)}/upload?${params}`), {
        method: "POST",
        body: form,
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        return { data: null, error: normalizeError(json?.error || json || res.statusText) };
      }
      return { data: json?.data ?? null, error: json?.error ?? null };
    } catch (err) {
      return { data: null, error: normalizeError(err) };
    }
  }

  getPublicUrl(path: string): { data: { publicUrl: string } } {
    const cleanPath = path.replace(/^\/+/, "");
    return {
      data: {
        publicUrl: `${publicOrigin()}/storage/v1/object/public/${encodeURIComponent(this.bucket)}/${cleanPath}`,
      },
    };
  }

  async remove(paths: string[]): Promise<ApiResult<null>> {
    try {
      const res = await fetch(apiUrl(`/api/storage/${encodeURIComponent(this.bucket)}/remove`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paths }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        return { data: null, error: normalizeError(json?.error || json || res.statusText) };
      }
      return { data: null, error: json?.error ?? null };
    } catch (err) {
      return { data: null, error: normalizeError(err) };
    }
  }
}

type RealtimeChannel = {
  on: (...args: unknown[]) => RealtimeChannel;
  subscribe: (callback?: (status: string) => void) => RealtimeChannel;
};

export const supabase = {
  from<T = any>(table: string) {
    return new LocalQueryBuilder<T>(table);
  },

  rpc<T = any>(name: string, body?: Record<string, unknown>): Promise<ApiResult<T>> {
    return fetch(apiUrl(`/api/rpc/${encodeURIComponent(name)}`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body || {}),
    })
      .then(async (res) => {
        const json = await res.json().catch(() => null);
        if (!res.ok) return { data: null, error: normalizeError(json?.error || json || res.statusText) };
        return { data: json?.data ?? null, error: json?.error ?? null };
      })
      .catch((err) => ({ data: null, error: normalizeError(err) }));
  },

  storage: {
    from(bucket: string) {
      return new LocalStorageBucket(bucket);
    },
  },

  functions: {
    async invoke<T = any>(name: string, options?: { body?: unknown }): Promise<ApiResult<T>> {
      try {
        const res = await fetch(apiUrl(`/functions/v1/${encodeURIComponent(name)}`), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(options?.body || {}),
        });
        const contentType = res.headers.get("content-type") || "";
        const data = contentType.includes("application/json") ? await res.json() : await res.text();
        if (!res.ok) return { data: null, error: normalizeError((data as { error?: unknown })?.error || data) };
        return { data: data as T, error: null };
      } catch (err) {
        return { data: null, error: normalizeError(err) };
      }
    },
  },

  channel(_name: string, _options?: unknown): RealtimeChannel {
    const channel: RealtimeChannel = {
      on: () => channel,
      subscribe: (callback?: (status: string) => void) => {
        window.setTimeout(() => callback?.("SUBSCRIBED"), 0);
        return channel;
      },
    };
    return channel;
  },

  removeChannel(_channel: RealtimeChannel) {
    return Promise.resolve({ error: null });
  },
};
